import React, { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { PartialForm } from '../../../../utils/form-utils';
import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { FileSelectionIcon } from '../../file-selection-icon/file-selection-icon';
import { Icon } from '../../icon/icon';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { GcpRow, isInvalidGcpRow, parseGcpFileContent } from '../gcp-parser';
import { ViewGcpDialog } from './view-gcp-dialog';

type TValue = string | undefined | null;
type GcpFileForm = PartialForm<{ gcpFilename: TValue }, TValue>;

type Props = {
  form: GcpFileForm;
  gcpPointsRef: MutableRefObject<GcpRow[]>;
  disabled: boolean;
  required: boolean;
};

export const GcpFileFormElement = (props: Props) => {
  const viewGcpDialog = useDialog();
  const theme = useTheme();
  const gcpFileSelected =
    isDefined(props.form.getValues('gcpFilename')) && props.form.getValues('gcpFilename');

  const { t } = useTranslation();
  return (
    <LabelledContainer
      required={props.required}
      text={t('gcpFile.title', { ns: 'cloudProcessing' })}
    >
      {(formElementId) => {
        const id = formElementId();

        return (
          <Stack direction="column" spacing={0.2}>
            <Stack alignItems="baseline" direction="row" spacing={0.5}>
              <TextBox
                {...props.form.register('gcpFilename', {
                  required: props.required,
                  validate: () => {
                    const hasErrors = props.gcpPointsRef.current
                      .filter((x) => x.included)
                      .some(isInvalidGcpRow);

                    return hasErrors
                      ? t('gcpFile.contentNeedsToBeChecked', { ns: 'cloudProcessing' })
                      : true;
                  },
                })}
                disabled={props.disabled}
                errorMessage={props.form.formState.errors.gcpFilename?.message}
                id={id}
                placeholder="- Ej vald -"
                readOnly={true}
                readOnlyCursor={gcpFileSelected ? 'pointer' : 'default'}
                value={props.form.getValues('gcpFilename') ?? undefined}
                onClick={
                  gcpFileSelected
                    ? () => {
                        viewGcpDialog.show();
                      }
                    : undefined
                }
              />
              <FileSelectionIcon
                accept=".txt,.csv"
                color={theme.color.gray.dark}
                fixedWidth={true}
                icon={['fad', 'file']}
                multiple={false}
                title={t('pickFile', { ns: 'common' })}
                onFilesSelected={async (files) => {
                  const rows = parseGcpFileContent(await files[0].text());
                  props.gcpPointsRef.current = rows;

                  // Add file to form. Also trig validation for same input in order to
                  // display `name` property of object (value in TextBox).
                  props.form.setValue('gcpFilename', files[0].name);
                  await props.form.trigger('gcpFilename');
                }}
                onHoverStyle={{ icon: ['fas', 'file'] }}
              />

              <Icon
                color={theme.color.gray.dark}
                disabled={!gcpFileSelected}
                fixedWidth={true}
                icon={['fad', 'trash-alt']}
                title={t('delete', { ns: 'common' })}
                onClick={async () => {
                  props.gcpPointsRef.current = [];
                  props.form.setValue('gcpFilename', null);
                  await props.form.trigger('gcpFilename');
                }}
                onHoverStyle={{ icon: ['fas', 'trash-alt'] }}
              />
            </Stack>
            {props.gcpPointsRef.current.length > 0 && (
              <span style={{ fontSize: '12px' }}>
                {t('gcpFile.clickToShowContent', { ns: 'cloudProcessing' })}
              </span>
            )}
            {viewGcpDialog.render(
              <ViewGcpDialog
                rows={props.gcpPointsRef.current}
                onClose={viewGcpDialog.hide}
                onSave={async (rows) => {
                  props.gcpPointsRef.current = rows;
                  await props.form.trigger('gcpFilename');
                  viewGcpDialog.hide();
                }}
              />,
            )}
          </Stack>
        );
      }}
    </LabelledContainer>
  );
};
